<script lang="ts">
  import { X } from "@lucide/svelte";
  import { ElementRect, useEventListener } from "runed";
  import OsmRelatedElementsList from "./OsmRelatedElementsList.svelte";
  import OsmTagTable from "./OsmTagTable.svelte";
  import SelectionDetails from "./SelectionDetails.svelte";
  import ExternalAppsMenu from "../ExternalAppsMenu.svelte";
  import type { OsmState } from "../MapScreen/selection/osmLookup.ts";
  import { selectionPanelViewModel } from "./viewModel.ts";
  import { Spinner } from "$components/ui/spinner/index.ts";
  import { Button } from "$components/ui/button/index.ts";
  import * as Alert from "$components/ui/alert/index.ts";
  import { runtimeConfig } from "../../config.ts";
  import type { LocatedOsmElement } from "../../lib/osm.ts";

  let {
    osm,
    occupiedDesktopWidth = $bindable(0),
    onclose,
    onselect,
  }: {
    osm: OsmState;
    occupiedDesktopWidth?: number;
    onclose: () => void;
    onselect: (next: LocatedOsmElement) => void;
  } = $props();

  const viewModel = $derived(
    osm.status === "idle"
      ? undefined
      : selectionPanelViewModel(osm, runtimeConfig.osmBaseUrl),
  );

  let panelElement = $state<HTMLElement | undefined>(undefined);
  const desktopMediaQuery = globalThis.matchMedia("(min-width: 640px)");
  const panelRect = new ElementRect(() => panelElement);

  function updateOccupiedDesktopWidth(): void {
    if (!panelElement || !desktopMediaQuery.matches) {
      occupiedDesktopWidth = 0;
      return;
    }

    // Use the panel's right edge, not only its width, because sibling overlays
    // also need to avoid the desktop left margin before the panel starts.
    occupiedDesktopWidth = Math.ceil(panelRect.right);
  }

  useEventListener(() => desktopMediaQuery, "change", () => {
    updateOccupiedDesktopWidth();
  });

  $effect(() => {
    updateOccupiedDesktopWidth();

    return () => {
      occupiedDesktopWidth = 0;
    };
  });
</script>

{#if viewModel}
  <aside
    class="fixed inset-x-3 bottom-3 z-[4] flex max-h-[72dvh] overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-xl sm:top-20 sm:bottom-4 sm:left-4 sm:right-auto sm:max-h-none sm:w-[390px]"
    aria-label="Selected place"
    bind:this={panelElement}
  >
    <div class="flex min-h-0 min-w-0 flex-1 flex-col">
      <header class="flex items-start gap-3 border-b border-border px-4 py-4">
        <div class="min-w-0 flex-1">
          <h2 class="truncate text-lg font-semibold leading-tight">
            {viewModel.title}
          </h2>
          <p class="mt-1 text-sm text-muted-foreground">
            {viewModel.subtitle}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          type="button"
          class="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Close selected place"
          onclick={onclose}
        >
          <X />
        </Button>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto py-4">
        {#if osm.status === "loading"}
          <div
            class="flex items-center gap-2 px-4 text-sm text-muted-foreground"
          >
            <Spinner class="size-4" />
            Loading details…
          </div>
        {:else if osm.status === "error"}
          <Alert.Root variant="destructive" class="mx-4">
            <Alert.Title>Failed to load details</Alert.Title>
            <Alert.Description>{osm.message}</Alert.Description>
          </Alert.Root>
        {:else if osm.status === "empty"}
          <p class="px-4 text-sm text-muted-foreground">
            No matching element was found near this feature.
          </p>
        {:else if viewModel.details && osm.status === "success"}
          <SelectionDetails
            details={viewModel.details}
            lngLat={osm.active.lnglat}
          />
        {/if}

        <OsmRelatedElementsList
          title="Nearby"
          elements={viewModel.nearbyPlaces}
          onSelect={onselect}
        />

        <OsmRelatedElementsList
          title="Also here"
          elements={viewModel.contextFeatures}
          onSelect={onselect}
          collapsed
        />

        {#if viewModel.details && osm.status === "success"}
          <OsmTagTable
            entries={viewModel.tagEntries}
            osmHref={viewModel.osmHref}
          />
          <div class="px-4 pt-4">
            <ExternalAppsMenu
              target={{
                kind: "poi",
                lngLat: osm.active.lnglat,
                label: viewModel.details.name,
                addressLines: viewModel.details.addressLines,
                osmElement: osm.active,
              }}
            />
          </div>
        {/if}
      </div>
    </div>
  </aside>
{/if}
